/**
 * A switch, not a set of options: there are two themes and one of them is on.
 * role="switch" is what makes a screen reader announce "Dark mode, switch, on"
 * rather than leaving the user to infer state from an icon.
 *
 * Nothing is written to storage until a choice is actually made, so a first visit
 * keeps following the OS -- including when it flips at sunset.
 */

import { useEffect, useState } from "react";

import { useI18n } from "../i18n";

const KEY = "petpoint-theme";

function stored(): "light" | "dark" | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "dark" || v === "light" ? v : null;
  } catch {
    return null; // private mode
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeToggle() {
  const { t } = useI18n();
  const [dark, setDark] = useState(() => stored() === "dark" || (!stored() && systemPrefersDark()));

  // Follow the OS only while no explicit choice has been made.
  useEffect(() => {
    if (stored()) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  function toggle() {
    const next = !dark;
    setDark(next);
    try {
      localStorage.setItem(KEY, next ? "dark" : "light");
    } catch {
      /* private mode: the choice lasts for this page only */
    }
    document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
  }

  return (
    <button
      type="button"
      className="theme-toggle"
      role="switch"
      aria-checked={dark}
      title={t("theme.dark")}
      onClick={toggle}
    >
      <span className="sr-only">{t("theme.dark")}</span>
      <span className="theme-track" aria-hidden="true">
        <svg className="theme-icon sun" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2.8v2.2M12 19v2.2M2.8 12h2.2M19 12h2.2M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6" />
        </svg>
        <svg className="theme-icon moon" viewBox="0 0 24 24">
          <path d="M20 14.4A8.4 8.4 0 0 1 9.6 4a8.4 8.4 0 1 0 10.4 10.4z" />
        </svg>
        <span className="theme-thumb" />
      </span>
    </button>
  );
}
